import { Button } from "@/components/ui/button";
import { Check, Sparkles, ArrowUpRight, Crown, Zap, Shield, Star, TrendingUp, Plus, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import AnimatedSection from "@/components/AnimatedSection";

const Pricing = () => {
  const { t, language } = useLanguage();

  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const plans = [
    {
      name: { fr: "Starter", en: "Starter" },
      description: {
        fr: "Idéal pour tester le GEO",
        en: "Perfect to test GEO",
      },
      price: { fr: "890", en: "890" },
      originalPrice: { fr: "990", en: "990" },
      discountPercent: "-10%",
      discountAmount: { fr: "100€ économisés", en: "Save €100" },
      spotsLeft: 5,
      includesPrevious: null,
      features: [
        { fr: "Audit GEO initial complet", en: "Complete initial GEO audit" },
        { fr: "Optimisation de 10 requêtes clés", en: "Optimization of 10 key queries" },
        { fr: "Dashboard de suivi basique", en: "Basic tracking dashboard" },
        { fr: "Support par email", en: "Email support" },
        { fr: "Rapport mensuel", en: "Monthly report" },
      ],
      popular: false,
      badge: null,
      icon: Zap,
      color: "from-sky-400 to-blue-500",
      accentColor: "sky",
      cardClass: "card-highlight-blue",
      buttonVariant: "outline" as const,
    },
    {
      name: { fr: "Growth", en: "Growth" },
      description: {
        fr: "Pour les entreprises en croissance",
        en: "For growing businesses",
      },
      price: { fr: "1 490", en: "1,490" },
      originalPrice: { fr: "2 129", en: "2,129" },
      discountPercent: "-30%",
      discountAmount: { fr: "639€ économisés", en: "Save €639" },
      spotsLeft: 3,
      includesPrevious: { fr: "Tout le Starter +", en: "All Starter +" },
      features: [
        { fr: "Optimisation de 30 requêtes", en: "Optimization of 30 queries" },
        { fr: "Dashboard avancé + API", en: "Advanced dashboard + API" },
        { fr: "Support prioritaire", en: "Priority support" },
        { fr: "Rapports hebdomadaires", en: "Weekly reports" },
        { fr: "Formation équipe (2h/mois)", en: "Team training (2h/month)" },
      ],
      popular: true,
      badge: { fr: "Le plus populaire", en: "Most popular" },
      icon: TrendingUp,
      color: "from-purple-500 to-violet-500",
      accentColor: "purple",
      cardClass: "card-highlight-purple",
      buttonVariant: "default" as const,
    },
    {
      name: { fr: "Enterprise", en: "Enterprise" },
      description: {
        fr: "Solution sur-mesure",
        en: "Custom solution",
      },
      price: { fr: "Sur devis", en: "Custom" },
      originalPrice: null,
      discountPercent: null,
      discountAmount: null,
      spotsLeft: null,
      includesPrevious: { fr: "Tout le Growth +", en: "All Growth +" },
      features: [
        { fr: "Optimisation illimitée", en: "Unlimited optimization" },
        { fr: "Dashboard white-label", en: "White-label dashboard" },
        { fr: "Account manager dédié", en: "Dedicated account manager" },
        { fr: "SLA garanti", en: "Guaranteed SLA" },
        { fr: "Stratégie multi-marchés", en: "Multi-market strategy" },
      ],
      popular: false,
      badge: { fr: "Premium", en: "Premium" },
      icon: Shield,
      color: "from-amber-500 to-orange-500",
      accentColor: "amber",
      cardClass: "card-highlight-orange",
      buttonVariant: "outline" as const,
    },
  ];

  return (
    <section id="pricing" className="py-12 lg:py-16 bg-slate-50/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-sky-100 to-purple-100 border border-sky-200/50 backdrop-blur-sm mb-6 shadow-sm">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span className="text-sm font-semibold bg-gradient-to-r from-sky-600 to-purple-600 bg-clip-text text-transparent">
              {language === "fr" ? "Tarification transparente" : "Transparent pricing"}
            </span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4 text-slate-800">
            {language === "fr" ? "Choisissez votre " : "Choose your "}
            <span className="bg-gradient-to-r from-sky-400 via-purple-500 to-sky-400 bg-clip-text text-transparent animate-gradient-shine">
              {language === "fr" ? "forfait" : "plan"}
            </span>
          </h2>
          <p className="text-lg text-slate-500 max-w-xl mx-auto">
            {t("pricing.subtitle")}
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 max-w-6xl mx-auto items-stretch pt-6">
          {plans.map((plan, index) => (
            <AnimatedSection
              key={index}
              animation="scale"
              delay={index * 0.12}
              className={`overflow-visible ${plan.popular ? "md:scale-[1.02] md:z-10" : ""}`}
            >
              <div
                className={`group relative rounded-2xl ${plan.cardClass} !overflow-visible transition-all duration-500 hover:-translate-y-2 flex flex-col h-full ${
                  plan.popular
                    ? "ring-2 ring-purple-400/50 shadow-xl shadow-purple-500/15 p-7 lg:p-9"
                    : "p-6 lg:p-8"
                }`}
              >
                {/* Badge visible */}
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-40">
                    <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold shadow-xl ${
                      plan.popular 
                        ? "bg-purple-600 text-white shadow-purple-600/40" 
                        : "bg-amber-500 text-slate-900 shadow-amber-500/40"
                    }`} style={{ color: plan.popular ? '#ffffff' : '#1e293b' }}>
                      {plan.popular ? <Crown className="w-4 h-4" /> : <Star className="w-4 h-4" />}
                      {plan.badge[language]}
                    </span>
                  </div>
                )}
                
                {/* Glass shine effect - subtle */}
                <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                  <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
                </div>
                
                {/* Glow effect on hover */}
                <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}>
                  <div className={`absolute -inset-0.5 bg-gradient-to-r ${plan.color} rounded-2xl blur-md opacity-20`} />
                </div>

                <div className="relative z-10 flex flex-col h-full">
                  {/* Header with icon */}
                  <div className="text-center mb-6">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${plan.color} mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <plan.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-display font-bold text-slate-800">
                      {plan.name[language]}
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      {plan.description[language]}
                    </p>
                  </div>

                  {/* Price section - fixed height to align buttons */}
                  <div className="text-center mb-6 pb-6 border-b border-slate-200/60 min-h-[100px] flex flex-col justify-end">
                    {/* Original price and discount badge */}
                    {plan.originalPrice ? (
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <span className="text-slate-400 text-lg line-through">
                          {plan.originalPrice[language]}€
                        </span>
                        {(plan.discountPercent || plan.discountAmount) && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200">
                            {plan.discountPercent && <span>{plan.discountPercent}</span>}
                            {plan.discountPercent && plan.discountAmount && <span>•</span>}
                            {plan.discountAmount && <span>{plan.discountAmount[language]}</span>}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="mb-2 h-7" /> 
                    )}
                    
                    {/* Main price */}
                    <div className="flex items-baseline justify-center gap-1">
                      <span className={`text-4xl lg:text-5xl font-display font-bold bg-gradient-to-r ${plan.color} bg-clip-text text-transparent transition-transform duration-300 inline-block group-hover:scale-105`}>
                        {plan.price[language]}
                      </span>
                      {plan.originalPrice && (
                        <span className={`text-xl font-bold bg-gradient-to-r ${plan.color} bg-clip-text text-transparent`}>€</span>
                      )}
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={scrollToContact}
                    className={`w-full font-semibold py-5 rounded-full transition-all duration-300 hover:scale-[1.02] mb-5 ${
                      plan.popular 
                        ? "bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-400 hover:to-violet-500 text-white shadow-lg shadow-purple-500/30" 
                        : index === 0
                          ? "bg-gradient-to-r from-sky-400 to-blue-500 hover:from-sky-300 hover:to-blue-400 text-white shadow-lg shadow-sky-500/25"
                          : "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white shadow-lg shadow-amber-500/25"
                    }`}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {language === "fr" ? `Choisir ${plan.name[language]}` : `Choose ${plan.name[language]}`}
                  </Button>

                  {/* Urgency & Guarantee */}
                  <div className="space-y-2 mb-5 text-center">
                    {plan.spotsLeft && (
                      <div className="flex items-center justify-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                        <span className="text-sm font-medium text-amber-600">
                          {plan.spotsLeft} {language === "fr" ? "places disponibles" : "spots available"}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500" />
                      <span className="text-sm font-medium text-purple-600">
                        {language === "fr" ? "Garantie satisfait ou remboursé" : "Money-back guarantee"}
                      </span>
                    </div>
                  </div>

                  {/* Includes previous plan badge */}
                  {plan.includesPrevious && (
                    <div className="mb-4 text-center">
                      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r ${plan.color} bg-opacity-10 text-slate-700 border border-slate-200`} style={{ background: `linear-gradient(to right, ${plan.accentColor === 'purple' ? 'rgb(243 232 255)' : plan.accentColor === 'amber' ? 'rgb(254 243 199)' : 'rgb(224 242 254)'}, ${plan.accentColor === 'purple' ? 'rgb(243 232 255)' : plan.accentColor === 'amber' ? 'rgb(254 243 199)' : 'rgb(224 242 254)'})` }}>
                        {plan.includesPrevious[language]}
                      </span>
                    </div>
                  )}

                  {/* Features */}
                  <ul className="space-y-3 flex-grow">
                    {plan.features.map((feature, idx) => (
                      <li 
                        key={idx} 
                        className="flex items-start gap-3 transition-all duration-300 group-hover:translate-x-1"
                        style={{ transitionDelay: `${idx * 0.03}s` }}
                      >
                        <div className={`flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r ${plan.color} flex items-center justify-center mt-0.5 shadow-sm`}>
                          <Check className="h-3 w-3 text-white" />
                        </div>
                        <span className="text-sm text-slate-600 group-hover:text-slate-700 transition-colors">
                          {feature[language]}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Trust badges */}
        <AnimatedSection className="mt-12 text-center" delay={0.5}>
          <div className="inline-flex flex-wrap items-center justify-center gap-4 lg:gap-8 px-6 py-4 rounded-2xl bg-white/60 backdrop-blur-sm border border-slate-200/50 shadow-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span className="text-sm font-medium">
                {language === "fr" ? "Garantie 30 jours" : "30-day guarantee"}
              </span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-300" />
            <div className="flex items-center gap-2 text-slate-600">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium">
                {language === "fr" ? "Mise en place rapide" : "Quick setup"}
              </span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-300" />
            <div className="flex items-center gap-2 text-slate-600">
              <Star className="w-4 h-4 text-purple-500" />
              <span className="text-sm font-medium">
                {language === "fr" ? "Support dédié" : "Dedicated support"}
              </span>
            </div>
          </div>
          
          <p className="text-sm text-slate-400 mt-6">
            {language === "fr"
              ? "Besoin d'une solution sur-mesure ? "
              : "Need a custom solution? "}
            <button
              onClick={scrollToContact}
              className="text-purple-500 hover:text-purple-600 font-medium transition-colors duration-300 underline underline-offset-2"
            >
              {language === "fr" ? "Contactez-nous" : "Contact us"}
            </button>
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Pricing;